import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { apiService } from '../services/api';
import { config } from '../config/index';
import './FileUploader.css';

interface FileUploaderProps {
  onUploadComplete: (result: any) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const getFileIcon = (extension: string) => {
  const icons: { [key: string]: string } = {
    'txt': '📄',
    'pdf': '📕',
    'docx': '📘',
    'rtf': '📝',
    'csv': '📊',
    'json': '{ }'
  };
  return icons[extension] || '📎';
};

export const FileUploader: React.FC<FileUploaderProps> = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  const pollJobStatus = useCallback(async (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await apiService.getJobStatus(jobId);
        
        setStatus(`Traitement: ${status.status}`);
        
        if (status.status === 'COMPLETED') {
          clearInterval(pollInterval);
          onUploadComplete(status);
          setUploading(false);
          setStatus('Terminé!');
          setJobId(null);
          setCurrentFile(null);
          
          // Clear success message after 3 seconds
          setTimeout(() => {
            setStatus('');
          }, 3000);
          
        } else if (status.status === 'FAILED') {
          clearInterval(pollInterval);
          setError(status.error_message || 'Erreur lors du traitement');
          setUploading(false);
          setJobId(null);
          setCurrentFile(null);
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    }, 2000);
    
    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (uploading) {
        setError('Timeout - le traitement prend trop de temps');
        setUploading(false);
      }
    }, 300000);
  }, [onUploadComplete, uploading]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setError(null);
    setStatus('');
    setCurrentFile(file);
    
    // Validate file size
    if (file.size > config.app.maxFileSize) {
      setError(`Fichier trop volumineux. Taille max: ${formatFileSize(config.app.maxFileSize)}`);
      return;
    }

    // Validate file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !config.app.supportedFormats.includes(extension)) {
      setError(`Format non supporté. Formats acceptés: ${config.app.supportedFormats.join(', ')}`);
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      setStatus('Upload en cours...');
      const response = await apiService.uploadFile(file, (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 100)
        );
        setProgress(percentCompleted);
      });

      if (response.success) {
        setJobId(response.job_id);
        setStatus('Fichier uploadé, traitement en cours...');
        setProgress(100);
        
        // Start polling for job status
        pollJobStatus(response.job_id);
      } else {
        throw new Error('Upload failed');
      }
      
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'upload');
      setUploading(false);
      setCurrentFile(null);
    }
  }, [pollJobStatus]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/rtf': ['.rtf'],
      'text/csv': ['.csv'],
      'application/json': ['.json']
    },
    maxSize: config.app.maxFileSize,
    multiple: false,
    disabled: uploading
  });

  return (
    <div className="file-uploader">
      <div 
        {...getRootProps()} 
        className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'disabled' : ''}`}
      >
        <input {...getInputProps()} />
        
        {uploading ? (
          <div className="upload-progress">
            <Loader className="spinner" size={48} />
            {currentFile && (
              <div className="file-info">
                <span className="file-icon">{getFileIcon(currentFile.name.split('.').pop()?.toLowerCase() || '')}</span>
                <span className="file-name">{currentFile.name}</span>
                <span className="file-size">({formatFileSize(currentFile.size)})</span>
              </div>
            )}
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-text">{progress}%</span>
            </div>
            {status && <p className="status-text">{status}</p>}
            {jobId && <p className="job-id">ID: {jobId}</p>}
          </div>
        ) : (
          <>
            {status === 'Terminé!' ? (
              <>
                <CheckCircle size={48} className="icon success" />
                <p className="main-text success">{status}</p>
              </>
            ) : (
              <>
                <Upload size={48} className="icon" />
                <p className="main-text">
                  {isDragActive 
                    ? "Déposez le fichier ici"
                    : "Glissez un fichier ou cliquez pour sélectionner"
                  }
                </p>
                <div className="formats">
                  <p>Formats supportés:</p>
                  <div className="format-list">
                    {config.app.supportedFormats.map((format: string) => (
                      <span key={format} className="format-badge">
                        {getFileIcon(format)} .{format}
                      </span>
                    ))}
                  </div>
                  <p className="size-limit">Taille max: {formatFileSize(config.app.maxFileSize)}</p>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};